import {
  useNetworkStatus, nsm, signalColor, signalBars, fmtHandshake,
} from "@/hooks/useNetworkStatus";
import {
  Signal, Wifi, RadioTower, Cable, WifiOff, ShieldCheck, ShieldAlert,
  Globe, Loader2, AlertTriangle, Router,
} from "lucide-react";

// Icône du transport actif.
const TransportIcon = ({ transport, size = 13, color }) => {
  const p = { size, style: { color } };
  if (transport === "4g") return <RadioTower {...p} />;
  if (transport === "wifi") return <Wifi {...p} />;
  if (transport === "ethernet") return <Cable {...p} />;
  return <WifiOff {...p} />;
};

// Barres de signal (4 barres) colorées selon le %.
const SignalBars = ({ pct, testid }) => {
  const bars = signalBars(pct);
  const col = signalColor(pct);
  return (
    <span data-testid={testid} className="inline-flex items-end gap-[2px]" title={pct != null ? `${pct}%` : "—"}>
      {[1, 2, 3, 4].map((i) => (
        <span key={i} className="w-[3px] rounded-[1px]"
          style={{ height: 3 + i * 2.5, background: i <= bars ? col : "rgba(255,255,255,0.15)" }} />
      ))}
    </span>
  );
};

// Pastille de statut de connexion.
export const NetworkBadge = ({ status, label, testid = "net-badge" }) => {
  const meta = nsm(status);
  return (
    <span data-testid={testid}
      className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider"
      style={{ borderColor: meta.color + "66", color: meta.color, background: meta.color + "10" }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.color }} />
      {label || meta.label}
    </span>
  );
};

const Row = ({ label, children, testid }) => (
  <div data-testid={testid} className="flex items-center justify-between gap-2 py-0.5">
    <span className="font-mono text-[9px] uppercase tracking-wider text-[#737373]">{label}</span>
    <span className="font-mono text-[10px] text-[#E5E5E5] text-right">{children}</span>
  </div>
);

// -------------------------------------------------------------------------
// Widget réseau réutilisable.
//  variant="panel" : bloc complet (fiche drone / cockpit)
//  variant="badge" : pastille compacte (overlay cockpit)
//  variant="mini"  : résumé popup carte
// -------------------------------------------------------------------------
export const NetworkStatusWidget = ({ droneId, variant = "panel", enabled = true, interval = 10000, netData }) => {
  // netData permet d'injecter des données (tests) ; sinon fetch autonome.
  const hook = useNetworkStatus(droneId, { enabled: enabled && !netData, interval });
  const data = netData || hook.net;
  const loading = !netData && hook.loading;

  const unreachable = data && data.reachable === false;
  const n = data?.network || {};
  const status = data?.status || "unknown";
  const activeSignal = n.signal_percent;

  // ---------- BADGE (overlay cockpit) ----------
  if (variant === "badge") {
    const meta = nsm(status);
    return (
      <span data-testid="net-badge-compact"
        className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider"
        style={{ borderColor: meta.color + "66", color: meta.color, background: "rgba(0,0,0,0.35)" }}>
        <TransportIcon transport={n.active_transport} size={11} color={meta.color} />
        {unreachable ? "Réseau —" : (n.active_label || "Réseau")}
        {activeSignal != null && <><SignalBars pct={activeSignal} /> {activeSignal}%</>}
        {n.wireguard?.connected ? <ShieldCheck size={11} className="text-[#22C55E]" />
          : <ShieldAlert size={11} className="text-[#F59E0B]" />}
      </span>
    );
  }

  // ---------- MINI (popup carte) ----------
  if (variant === "mini") {
    if (loading && !data) return <div className="font-mono text-[10px] text-[#A3A3A3] mt-1">Réseau…</div>;
    if (unreachable) return <div data-testid="map-net-unreachable" className="font-mono text-[10px] text-[#F59E0B] mt-1">Réseau: injoignable</div>;
    return (
      <div data-testid="map-net" className="mt-1.5 pt-1.5 border-t border-black/10">
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: nsm(status).color }} />
          <span className="font-bold">{n.active_label || "Réseau"}</span>
          {activeSignal != null && <span style={{ color: signalColor(activeSignal) }}>{activeSignal}%</span>}
        </div>
        <div className="flex items-center gap-2 text-[10px]">
          <span style={{ color: n.wireguard?.connected ? "#16a34a" : "#d97706" }}>
            VPN {n.wireguard?.connected ? "OK" : "KO"}
          </span>
          {n.wireguard?.latest_handshake_seconds != null &&
            <span className="text-[#666]">{fmtHandshake(n.wireguard.latest_handshake_seconds)}</span>}
        </div>
      </div>
    );
  }

  // ---------- PANEL (complet) ----------
  const wg = n.wireguard || {};
  const wifi = n.wifi || {};
  const wwan = n.wwan || {};
  const secondary = []; // secours disponibles
  if (n.active_transport !== "4g" && wwan.connected) secondary.push({ label: "4G Bouygues", sig: wwan.signal_percent });
  if (n.active_transport !== "wifi" && wifi.connected) secondary.push({ label: wifi.connection || "Wi-Fi", sig: wifi.signal_percent });

  return (
    <div data-testid="net-status-panel" className="bg-[#141414] border border-white/10 rounded-sm">
      <div className="px-3 py-2 flex items-center gap-2 border-b border-white/10">
        <Router size={12} className="text-[#A3A3A3]" />
        <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#A3A3A3]">Connexion réseau</span>
        <div className="ml-auto flex items-center gap-2">
          {loading && <Loader2 size={11} className="animate-spin text-[#3B82F6]" />}
          <NetworkBadge status={status} testid="net-global-badge" />
        </div>
      </div>

      {unreachable ? (
        <div data-testid="net-unreachable" className="p-3 flex items-center gap-2 font-mono text-[10px] text-[#F59E0B]">
          <AlertTriangle size={13} />
          {data?.not_deployed
            ? "Lecture réseau non déployée (network_status.py / Infra API 2.12+)"
            : `Réseau injoignable${data?.error ? ` — ${data.error}` : ""}`}
        </div>
      ) : (
        <div className="p-3 space-y-2.5">
          {/* Connexion active */}
          <div data-testid="net-active" className="flex items-center gap-2.5 rounded-sm border border-white/10 bg-black/30 px-2.5 py-2">
            <TransportIcon transport={n.active_transport} size={18} color={nsm(status).color} />
            <div className="flex flex-col leading-tight">
              <span className="font-mono text-[8px] uppercase tracking-wider text-[#737373]">Connexion active</span>
              <span className="font-mono text-[12px] font-bold text-white" data-testid="net-active-label">{n.active_label || "—"}</span>
            </div>
            <div className="ml-auto flex items-center gap-2">
              {activeSignal != null && <SignalBars pct={activeSignal} testid="net-active-bars" />}
              <span className="font-mono text-[13px] font-bold" style={{ color: signalColor(activeSignal) }} data-testid="net-active-signal">
                {activeSignal != null ? `${activeSignal}%` : "—"}
              </span>
            </div>
          </div>

          {/* Secours disponible */}
          {secondary.map((s, i) => (
            <div key={i} data-testid={`net-secondary-${i}`} className="flex items-center justify-between gap-2 font-mono text-[10px] text-[#A3A3A3] px-1">
              <span className="flex items-center gap-1.5"><Wifi size={11} /> Secours : {s.label}</span>
              <span style={{ color: signalColor(s.sig) }}>{s.sig != null ? `${s.sig}%` : "—"}</span>
            </div>
          ))}

          <div className="space-y-0.5 pt-1 border-t border-white/5">
            <Row label="Interface" testid="net-iface">{n.active_interface || "—"}</Row>
            <Row label="IP locale" testid="net-ip">{n.active_ip || "—"}</Row>
            <Row label="Route défaut" testid="net-route">
              {n.default_route?.gateway || "—"}
              {n.default_route?.metric != null && <span className="text-[#737373]"> (m{n.default_route.metric})</span>}
            </Row>
            {wwan.operator && <Row label="Opérateur" testid="net-operator">{wwan.operator}{wwan.access_tech ? ` · ${wwan.access_tech.toUpperCase()}` : ""}</Row>}
          </div>

          {/* WireGuard (tunnel logique, pas le transport) */}
          <div data-testid="net-wireguard" className="flex items-center gap-2 rounded-sm border border-white/10 bg-black/30 px-2.5 py-1.5">
            {wg.connected ? <ShieldCheck size={14} className="text-[#22C55E]" /> : <ShieldAlert size={14} className="text-[#F59E0B]" />}
            <span className="font-mono text-[10px]" style={{ color: wg.connected ? "#22C55E" : "#F59E0B" }}>
              VPN {wg.connected ? "connecté" : "déconnecté"}
            </span>
            {wg.vpn_ip && <span className="font-mono text-[10px] text-[#A3A3A3]">{wg.vpn_ip}</span>}
            <span className="ml-auto font-mono text-[9px] text-[#737373]">handshake {fmtHandshake(wg.latest_handshake_seconds)}</span>
          </div>
        </div>
      )}
    </div>
  );
};
